import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

import { useEmailVerification } from "@fuse/hooks/useEmailVerification"
import { useImageModal } from "@fuse/sharedComponents/globalImageModal"
import useShare from "@fuse/sharedComponents/globalShare/useShare"
import {
  showErrorToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import { TWebUser } from "@letshang/constants"
import { reload, signOut } from "firebase/auth"
import { get, isEmpty } from "lodash-es"
import { useRecoilState, useResetRecoilState } from "recoil"

import { recentViewEventData } from "@fuse/recoil/events"
import { defaultUser, screenState, userState } from "@fuse/recoil/signup"

import { auth } from "@firebaseConfig/clientApp"

import {
  deleteUserAPI,
  getUserInfo,
  resendEmailVerificationAPI,
  updateUserDetails,
} from "@api/firebaseWrappedApis"

import { GenderPronounMap } from "@fuse/utils/common"
import {
  devEmailVerificationUtils,
  DownloadLinkMap,
  isDev,
  useDeviceDetection,
} from "@fuse/utils/DeviceDection"
import { uploadImage } from "@fuse/utils/Firebase"

import PleadingGIF from "@fuse/assets/gif/gif-pleading.gif"
import IconBooks from "@fuse/assets/images/Profile/img-icon-books.svg"
import IconCamera from "@fuse/assets/images/Profile/img-icon-camera.svg"
import IconImage from "@fuse/assets/images/Profile/img-icon-image.svg"
import IconLogout from "@fuse/assets/images/Profile/img-icon-log-out.svg"
import IconSetting from "@fuse/assets/images/Profile/img-icon-setting.svg"
import IconUseradd from "@fuse/assets/images/Profile/img-icon-user-add.svg"

// DetailRow Component
export interface DetailRowProps {
  label: string
  value: string
  className?: string
  disabled?: boolean
  onClick?: () => void
}

export type AccordionOption = {
  label: string
  onPress: () => void
}

export type SettingOption = {
  icon: JSX.Element
  label: string
  isExpandable: boolean
  isOpen: boolean
  accordionOptions?: AccordionOption[]
  elIndex?: number
  isDestructive?: boolean
}

export type ProfilePictureOption = {
  icon: JSX.Element
  label: string
}

export enum EDetailsRowOperation {
  Email = "email",
  DOB = "DOB",
  Gender = "gender",
  ProfilePicture = "profilePicture",
}

const useProfileController = () => {
  const navigate = useNavigate()
  const shareContent = useShare()

  const resetter = [
    useResetRecoilState(userState),
    useResetRecoilState(screenState),
    useResetRecoilState(recentViewEventData),
  ]

  const { showImageAlert, hideImageAlert } = useImageModal()

  const [user, setUser] = useRecoilState(userState)
  const [, setScreen] = useRecoilState(screenState)

  const device = useDeviceDetection()
  const downloadLink = DownloadLinkMap[device]

  const [isLoading, setIsLoading] = useState(false)
  const [detailsRowOperation, setDetailsRowOperation] = useState("")
  const [isEmailInboxVerification, setIsEmailInboxVerification] =
    useState(false)

  // Use consolidated email verification
  const { verifyEmail, startPolling } = useEmailVerification()

  useEffect(() => {
    if (detailsRowOperation) {
      // Disable scrolling
      document.body.style.overflow = "hidden"
    } else {
      // Enable scrolling
      document.body.style.overflow = "auto"
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "auto"
    }
  }, [detailsRowOperation])

  const onDeleteAccount = async () => {
    setIsLoading(true)
    try {
      deleteUserAPI({})
        .then(() => {
          setUser(defaultUser)
          setScreen(1)
          window.location.href = "https://letshang.info/"
          showSuccessToast("Account deleted successfully!")
        })
        .finally(() => {
          setIsLoading(false)
        })
    } catch (err) {
      console.error(err)
      showErrorToast("Error on deleting account!")
    } finally {
      setIsLoading(false)
    }
  }

  const currentUser = auth.currentUser
  const userEmail = get(user, "email", "")
  const userDOB = get(user, "dateOfBirth", "")
  const isInvalidDOB = userDOB === "Invalid date"

  const profilePictureOptions: ProfilePictureOption[] = [
    {
      icon: <img src={IconImage} />,
      label: "Upload photo",
    },
    {
      icon: <img src={IconCamera} />,
      label: "Take photo",
    },
  ]

  const detailsRowHolder = [
    {
      label: "Email",
      value: get(user, "email", "Add your email") || "Add your email",
      disabled: isEmpty(userEmail)
        ? false
        : get(currentUser, "emailVerified", true),
    },
    {
      label: "Date of birth",
      value: !isInvalidDOB ? userDOB || "Add your DOB" : "Add your DOB",
      disabled: isInvalidDOB ? false : !isEmpty(get(user, "dateOfBirth")),
    },
    {
      label: "Gender",
      value: GenderPronounMap[user.gender] || "Add your gender",
      disabled: !isEmpty(user.gender) || user.gender === "rather-not-to-say",
    },
  ]

  const settingOptions: SettingOption[] = [
    {
      icon: <img src={IconUseradd} />,
      label: "Invite a friend",
      isExpandable: false,
      isOpen: false,
    },
    {
      icon: <img src={IconBooks} />,
      label: "Legal",
      isExpandable: true,
      isOpen: false,
      accordionOptions: [
        {
          label: "Privacy Policy",
          onPress: () => {
            window.open(
              "https://pages.flycricket.io/walkme/privacy.html",
              "_blank",
            )
          },
        },
        {
          label: "Terms of Use",
          onPress: () => {
            window.open(
              "https://pages.flycricket.io/walkme/terms.html",
              "_blank",
            )
          },
        },
      ],
    },
    {
      icon: <img src={IconSetting} />,
      label: "Manage account",
      isExpandable: true,
      isOpen: false,
      accordionOptions: [
        {
          label: "Delete account",
          onPress: () => {
            showImageAlert({
              image: PleadingGIF,
              title: "Are you sure you want to delete this account?",
              description:
                "Keep in mind, this will delete your account and data permanently.",
              okButtonTitle: "YES, DELETE IT ALL",
              okButtonPress: () => {
                onDeleteAccount()
                hideImageAlert()
              },
              cancelButtonTitle: "Cancel",
              cancelButtonPress: () => {},
            })
          },
        },
      ],
    },
    {
      icon: <img src={IconLogout} />,
      label: "Log out",
      isExpandable: false,
      isOpen: false,
      isDestructive: true,
    },
  ]

  const [settingHolder, setSettingHolder] = useState(settingOptions)

  const handleLogout = async () => {
    // 🔥 DEV: Clear fake email verification before logout
    const currentUser = auth.currentUser
    if (currentUser?.uid) {
      devEmailVerificationUtils.clearDevVerification(currentUser.uid)
    }

    await auth.signOut()
    setUser(defaultUser)
    setScreen(1)
    window.location.href = "https://letshang.info/"
    // Reset local storage
    localStorage.clear()

    // Reset Recoil states
    resetter.forEach((reset) => reset())
  }

  const onExpandSetting = (index: number) => {
    setSettingHolder((prevOptions) =>
      prevOptions.map((option, i) => {
        // Toggle the isOpen property only for the clicked index
        if (i === index) {
          return { ...option, isOpen: !option.isOpen }
        }
        return { ...option, isOpen: false } // Close others (optional behavior)
      }),
    )
  }

  const onSettingPress = (index: number) => {
    switch (index) {
      case 0:
        return shareContent({
          text: "Share this event",
          url: downloadLink,
          title: "Let's Hang!",
        })

      case 1:
        return onExpandSetting(index)

      case 2:
        return onExpandSetting(index)

      case 3:
        return handleLogout()

      default:
        break
    }
  }

  // Function to check email verification status periodically
  const checkVerificationState = async () => {
    // Set an interval to reload the Firebase auth state every 2 seconds
    const intervalId = setInterval(async () => {
      if (auth.currentUser) {
        await reload(auth.currentUser!)
      }
      if (auth.currentUser?.emailVerified) {
        setDetailsRowOperation("")
        setIsEmailInboxVerification(false)
        clearInterval(intervalId) // Stop checking if email is verified
      }
    }, 2000)

    // Clear the interval after 30 seconds (to prevent memory leaks)
    setTimeout(() => {
      clearInterval(intervalId)
    }, 30000)
  }

  const onVerifyEmail = async (email: string) => {
    checkVerificationState()

    const result = await verifyEmail(email, {
      onSuccess: () => {
        // Success callback for both dev and production flows
      },
      onCloseUI: () => {
        // Dev mode: immediate verification, close UI
        setDetailsRowOperation("")
        setIsEmailInboxVerification(false)
      },
      onRequireInboxCheck: () => {
        // Production mode: show inbox check UI and start polling
        setIsEmailInboxVerification(true)

        // Start polling with cleanup
        startPolling(() => {
          setDetailsRowOperation("")
          setIsEmailInboxVerification(false)
        })
      },
      onVerificationComplete: () => {
        // Handle completion callback if needed
      },
    })

    return result
  }

  const onResendMail = () => {
    setIsLoading(true)
    resendEmailVerificationAPI({})
      .then(() => {
        showSuccessToast("Mail sent successfully!")
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const onUpdateUserDetails = async (updatedValue: Record<string, string>) => {
    setIsLoading(true)
    updateUserDetails({
      ...user,
      name: user?.firstName + " " + user?.lastName,
      dateOfBirth: isInvalidDOB ? "" : user?.dateOfBirth,
      ...updatedValue,
    })
      .then((result) => {
        if (get(result, "success", false)) {
          setUser((prev: TWebUser) => ({
            ...prev,
            ...updatedValue,
          }))
          setDetailsRowOperation("")
        } else {
          showErrorToast(get(result, "message", "Something went wrong!"))
        }
      })
      .catch(() => {
        showErrorToast("Something went wrong!")
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const onDetailRawPress = (index: number) => {
    switch (index) {
      case 0:
        return setDetailsRowOperation(EDetailsRowOperation.Email)

      case 1:
        return setDetailsRowOperation(EDetailsRowOperation.DOB)

      case 2:
        return setDetailsRowOperation(EDetailsRowOperation.Gender)

      default:
        break
    }
  }

  const onEditProfilePicture = () => {
    setDetailsRowOperation("profilePicture")
  }

  const onUploadProfilePicture = (croppedImage: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      setIsLoading(true)

      const fileName = "profile.png"

      uploadImage(croppedImage, fileName, user)
        .then((result) => {
          // Update user's avatar URL
          setUser((prev: TWebUser) => ({
            ...prev,
            avatarUrl: result,
          }))

          resolve(result)
        })
        .catch((error) => {
          showErrorToast(error || "Something went wrong!")
          reject(error)
        })
        .finally(() => {
          setIsLoading(false) // Hide loading state
        })
    })
  }

  const onCloseDetailRowPopup = () => {
    setDetailsRowOperation("")
  }

  return {
    user,
    isLoading,
    settingHolder,
    onSettingPress,
    detailsRowOperation,
    detailsRowHolder,
    onDetailRawPress,
    onVerifyEmail,
    isEmailInboxVerification,
    onResendMail,
    onUpdateUserDetails,
    onCloseDetailRowPopup,
    onEditProfilePicture,
    profilePictureOptions,
    onUploadProfilePicture,
  }
}

export default useProfileController
